import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { formatIDR, formatDateID, formatDateLongID } from '../lib/format';
import { ArrowLeft, Send, CheckCircle2, XCircle, Loader2, FileDown, MessageCircle, ReceiptText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { buildWhatsAppQuotation } from '../lib/whatsapp';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { QuotationFormDialog } from '../components/QuotationFormDialog';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotations/${id}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (s) => {
    setBusy(true);
    try {
      await api.post(`/quotations/${id}/status`, null, { params: { new_status: s } });
      toast.success('Status diperbarui');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal'); }
    finally { setBusy(false); }
  };

  const convertToInvoice = async (force = false) => {
    setBusy(true);
    try {
      const { data: inv } = await api.post(`/quotations/${id}/convert-to-invoice`, null, { params: { force } });
      toast.success(`Invoice ${inv.invoice_number} berhasil dibuat`);
      navigate(`/invoices/${inv.id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Invoice untuk penawaran ini sudah pernah dibuat.');
        load();
      } else {
        toast.error(e?.response?.data?.detail || 'Gagal membuat invoice');
      }
    } finally { setBusy(false); }
  };

  const copyWhatsApp = async () => {
    if (!data) return;
    const msg = buildWhatsAppQuotation({
      client_name: data.client_name,
      event_description: data.event_description,
      event_date: data.event_date,
      venue: data.venue,
      quotation_number: data.quotation_number,
      total_amount: data.total_amount,
      valid_until_date: data.valid_until_date,
    });
    try {
      await navigator.clipboard.writeText(msg);
      toast.success('Pesan WhatsApp tersalin');
    } catch { toast.error('Gagal menyalin'); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${data?.quotation_number || 'quotation'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF belum tersedia');
    }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-32" /><Skeleton className="h-40" /><Skeleton className="h-60" /></div>;
  if (!data) return null;
  const canEdit = PERMISSIONS.canEdit(role);

  return (
    <div className="space-y-6" data-testid="quotation-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/quotations')}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-xl font-semibold font-mono-num">{data.quotation_number}</span>
            <StatusBadge status={data.status} />
          </div>
          <div className="text-sm text-muted-foreground">{data.client_name} • {formatDateLongID(data.quotation_date)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>}
          {canEdit && data.status === 'Draft' && <Button variant="secondary" onClick={() => setStatus('Sent')} disabled={busy}><Send className="h-4 w-4 mr-2" />Tandai Terkirim</Button>}
          {canEdit && data.status === 'Sent' && <Button variant="secondary" onClick={() => setStatus('Approved')} disabled={busy}><CheckCircle2 className="h-4 w-4 mr-2" />Disetujui</Button>}
          {canEdit && data.status === 'Sent' && <Button variant="secondary" onClick={() => setStatus('Rejected')} disabled={busy}><XCircle className="h-4 w-4 mr-2" />Ditolak</Button>}

          {/* Convert to Invoice — only when Approved AND no linked invoice yet */}
          {canEdit && data.status === 'Approved' && !data.linked_invoice && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={busy} data-testid="quotation-convert-to-invoice">
                  <ReceiptText className="h-4 w-4 mr-2" />Convert ke Invoice
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Buat invoice dari penawaran ini?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Data client, event, venue, item, dan total akan otomatis disalin ke invoice. Jatuh tempo default 7 hari setelah tanggal event.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Batal</AlertDialogCancel>
                  <AlertDialogAction onClick={() => convertToInvoice(false)} data-testid="quotation-convert-confirm">Buat Invoice</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Already converted — link to existing invoice */}
          {data.linked_invoice && (
            <Button
              onClick={() => navigate(`/invoices/${data.linked_invoice.id}`)}
              data-testid="quotation-view-linked-invoice"
            >
              <ExternalLink className="h-4 w-4 mr-2" />Lihat Invoice
            </Button>
          )}

          <Button variant="secondary" onClick={copyWhatsApp}><MessageCircle className="h-4 w-4 mr-2" />Pesan WA</Button>
          <Button variant="secondary" onClick={downloadPdf}><FileDown className="h-4 w-4 mr-2" />PDF</Button>
        </div>
      </div>

      {/* Linked invoice info card */}
      {data.linked_invoice && (
        <Card className="bg-primary/5 border-primary/30" data-testid="quotation-linked-invoice-card">
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-primary" />
              <div>
                <div className="text-xs text-muted-foreground">Invoice Terkait</div>
                <div className="font-mono-num font-semibold">{data.linked_invoice.invoice_number}</div>
              </div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-border" />
            <div>
              <div className="text-xs text-muted-foreground">Tanggal Invoice</div>
              <div className="text-sm">{formatDateID(data.linked_invoice.invoice_date)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Jatuh Tempo</div>
              <div className="text-sm">{formatDateID(data.linked_invoice.due_date)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Status</div>
              <div className="mt-0.5"><StatusBadge status={data.linked_invoice.status} /></div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Outstanding</div>
              <div className="text-sm font-mono-num font-semibold">{formatIDR(data.linked_invoice.outstanding_amount)}</div>
            </div>
            <div className="ml-auto">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/invoices/${data.linked_invoice.id}`)}
              >
                Buka <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Client & Event</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div><span className="text-muted-foreground">Client:</span> <span className="font-medium">{data.client?.client_name}</span></div>
            <div><span className="text-muted-foreground">PIC:</span> {data.client?.pic_name || '-'}</div>
            <div><span className="text-muted-foreground">Telepon:</span> {data.client?.phone_number || '-'}</div>
            <div><span className="text-muted-foreground">Event:</span> {data.event_description || '-'}</div>
            <div><span className="text-muted-foreground">Tgl Event:</span> {formatDateID(data.event_date)}</div>
            <div><span className="text-muted-foreground">Venue:</span> {data.venue || '-'}</div>
            <div><span className="text-muted-foreground">Berlaku Hingga:</span> {formatDateID(data.valid_until_date)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Ringkasan</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-muted-foreground">Jumlah Item:</span><span>{data.items?.length || 0}</span></div>
            <div className="flex justify-between text-base"><span className="font-semibold">Total</span><span className="font-semibold font-mono-num">{formatIDR(data.total_amount)}</span></div>
            {data.notes && <div className="mt-2"><div className="text-xs text-muted-foreground mb-1">Catatan</div><div className="text-sm whitespace-pre-wrap">{data.notes}</div></div>}
            {data.terms_conditions && <div className="mt-2"><div className="text-xs text-muted-foreground mb-1">Syarat & Ketentuan</div><div className="text-sm whitespace-pre-wrap">{data.terms_conditions}</div></div>}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Line Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Nama</th>
                  <th className="px-4 py-2 text-left">Deskripsi</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Harga</th>
                  <th className="px-4 py-2 text-right">Diskon</th>
                  <th className="px-4 py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {data.items?.map((it) => (
                  <tr key={it.id} className="border-t border-border">
                    <td className="px-4 py-2">{it.item_name}</td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-pre-wrap align-top">{it.description || '-'}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{it.quantity}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(it.unit_price)}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(it.discount)}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-border">
                <tr><td colSpan={5} className="px-4 py-2 text-right font-semibold">Total</td><td className="px-4 py-2 text-right font-mono-num font-semibold">{formatIDR(data.total_amount)}</td></tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <QuotationFormDialog open={editOpen} onOpenChange={setEditOpen} editing={data} onSaved={load} />
    </div>
  );
}
